/**
 * Session Completion Metrics Utility
 * Derives session completion summary metrics from message history
 * Used by SessionCompletionSummary component to display pebbles earned, streak, and improvement tips
 */

import type { Message, SessionSummary } from '@/types/session';
import { groupMessagesByQuestionCycle, type QuestionCycle } from '@/lib/questionCycles';

/**
 * Session completion metrics derived from message history or serverless summary
 */
export interface SessionCompletionMetrics {
  /** Total pebbles earned in this session (sum of all totalAwarded) */
  totalPebblesEarned: number;
  /** Number of consecutive questions answered correctly on first try */
  firstTryStreak: number;
  /** Total questions answered in the session */
  totalQuestions: number;
  /** Session-specific encouraging improvement tip */
  improvementTip: string;
  /** Detailed question summary for analytics */
  questionSummary: Array<{
    questionNumber: number;
    attemptCount: number;
    pebblesEarned: number;
  }>;
  /** Question types the student struggled with (serverless only) */
  questionTypesStruggled?: string[];
  /** Raw serverless session summary (if available) */
  rawSessionSummary?: SessionSummary;
}

/**
 * Calculate first-try streak count from question cycles
 * Counts consecutive questions from the start where attemptCount === 1
 * Stops counting at the first non-first-try question
 *
 * @param cycles - Array of question cycles from groupMessagesByQuestionCycle
 * @returns Number of consecutive first-try correct answers from start
 */
function calculateFirstTryStreak(cycles: QuestionCycle[]): number {
  let streak = 0;

  // Filter to numbered questions only (exclude intro)
  const numberedCycles = cycles.filter((c) => c.questionNumber !== null);

  // Sort by question number to ensure we count from the beginning
  const sortedCycles = [...numberedCycles].sort(
    (a, b) => (a.questionNumber ?? 0) - (b.questionNumber ?? 0)
  );

  for (const cycle of sortedCycles) {
    // Only count completed questions
    if (!cycle.isComplete) {
      break;
    }

    // Find the question_complete message in this cycle
    const completeMessage = cycle.messages.find((msg) => msg.decisionType === 'question_complete');

    // If no complete message or attemptCount is missing, treat as non-first-try
    if (!completeMessage || completeMessage.attemptCount === undefined) {
      break;
    }

    // If attemptCount is 1, increment streak; otherwise stop
    if (completeMessage.attemptCount === 1) {
      streak++;
    } else {
      break;
    }
  }

  return streak;
}

/**
 * Generate a session-specific improvement tip based on performance
 * Tips are encouraging, specific to the session data, and under 200 characters
 *
 * @param cycles - Question cycles with completion data
 * @param firstTryStreak - Number of consecutive first-try correct answers
 * @param totalQuestions - Total number of questions in the session
 * @returns Encouraging tip string (max 200 chars)
 */
function generateImprovementTip(firstTryStreak: number, totalQuestions: number): string {
  // Edge case: no questions answered
  if (totalQuestions === 0) {
    return 'Every practice session builds stronger reading skills. Keep it up!';
  }

  // Perfect first-try streak on all questions
  if (firstTryStreak === totalQuestions && totalQuestions > 0) {
    return 'You showed strong comprehension across all questions. Keep reading!';
  }

  // Strong partial streak (more than half)
  if (firstTryStreak > totalQuestions / 2 && firstTryStreak > 0) {
    return `You started strong with ${firstTryStreak} first-try answers. Keep building on that momentum!`;
  }

  // Some first-try successes but less than half
  if (firstTryStreak > 0) {
    return 'You showed good comprehension skills. Keep practicing to build your confidence!';
  }

  // No first-try successes but questions were answered
  return 'Persistence is the key to growth. Each question helps you learn something new!';
}

/**
 * Derive session completion metrics from the messages array or serverless summary
 * This is the main entry point for computing all completion summary data
 *
 * When sessionSummary is provided (serverless sessions), it is used as the primary data source.
 * When sessionSummary is not provided (MCP sessions), metrics are derived from messages.
 *
 * @param messages - Array of Message objects from useSessionMessages hook
 * @param sessionSummary - Optional serverless session summary with structured data
 * @returns SessionCompletionMetrics with all derived data
 *
 * @example
 * ```typescript
 * // Serverless mode - use structured summary
 * const metrics = deriveSessionCompletionMetrics(messages, sessionSummary);
 * console.log(metrics.totalPebblesEarned); // 24 (from sessionSummary)
 *
 * // MCP mode - derive from messages
 * const metrics = deriveSessionCompletionMetrics(messages);
 * console.log(metrics.totalPebblesEarned); // 24 (calculated from messages)
 * ```
 */
export function deriveSessionCompletionMetrics(
  messages: Message[] | undefined | null,
  sessionSummary?: SessionSummary | null
): SessionCompletionMetrics {
  // Serverless mode: use structured summary data directly
  if (sessionSummary && sessionSummary.questionsAttempted >= 0) {
    const totalPebblesEarned = sessionSummary.totalSessionPebbles;
    const firstTryStreak = sessionSummary.firstAttemptCorrect;
    const totalQuestions = sessionSummary.questionsAttempted;
    const questionTypesStruggled = sessionSummary.questionTypesStruggled || [];

    // Generate improvement tip using same logic as MCP mode
    const improvementTip = generateImprovementTip(firstTryStreak, totalQuestions);

    return {
      totalPebblesEarned,
      firstTryStreak,
      totalQuestions,
      improvementTip,
      questionSummary: [], // Not populated from serverless summary
      questionTypesStruggled,
      rawSessionSummary: sessionSummary,
    };
  }

  // MCP mode: derive from messages (existing logic)
  // Handle empty/null input with safe defaults
  if (!messages || messages.length === 0) {
    return {
      totalPebblesEarned: 0,
      firstTryStreak: 0,
      totalQuestions: 0,
      improvementTip: 'Every practice session builds stronger reading skills. Keep it up!',
      questionSummary: [],
    };
  }

  // Group messages into question cycles
  const { cycles, totalQuestions } = groupMessagesByQuestionCycle(messages);

  // Calculate total pebbles earned (sum all totalAwarded from question_complete messages)
  let totalPebblesEarned = 0;
  const questionSummary: Array<{
    questionNumber: number;
    attemptCount: number;
    pebblesEarned: number;
  }> = [];

  for (const cycle of cycles) {
    // Skip intro cycle (no question number)
    if (cycle.questionNumber === null) {
      continue;
    }

    // Extract pebbles data from this cycle
    if (cycle.pebbles) {
      totalPebblesEarned += cycle.pebbles.totalAwarded;
    }

    // Find question_complete message for attempt count
    const completeMessage = cycle.messages.find((msg) => msg.decisionType === 'question_complete');

    if (completeMessage) {
      questionSummary.push({
        questionNumber: cycle.questionNumber,
        attemptCount: completeMessage.attemptCount ?? 0,
        pebblesEarned: completeMessage.totalAwarded ?? 0,
      });
    }
  }

  // Calculate first-try streak
  const firstTryStreak = calculateFirstTryStreak(cycles);

  // Generate improvement tip
  const improvementTip = generateImprovementTip(firstTryStreak, totalQuestions);

  return {
    totalPebblesEarned,
    firstTryStreak,
    totalQuestions,
    improvementTip,
    questionSummary,
  };
}
